<app-side-dialog
  #dialog
  [headerText]="isEdit ? 'Edit Organization' : 'New Organization'"
  [saveText]="isEdit ? 'Save' : 'Create'"
  [busy]="loading"
  [headerGlyph]="ResourceType.ORGANIZATION | resourceGlyph"
  [body]="body"
  [validationFn]="validateForm.bind(this)"
  (save)="onSubmit()"
  [contentVisible]="!loading"
  [errorText]="
    this.isEdit ? 'Cannot edit organization' : 'Cannot create organization'
  "
  [errorMsg]="errorMsg"
></app-side-dialog>
<ng-template #body>
  @if (!isEdit) {
    <div class="uploader-container">
      <div class="uploader-wrap">
        <fd-file-uploader
          id="file-uploader"
          ariaLabel="Choose file"
          ariaLabelledBy="Choose file"
          i18n-placeholder="
            XMSG|Placeholder of the Upload Configuration File
            input@@create.file.uploadFilePlaceholder"
          i18n-buttonLabel="
            XBUT|Text of the Upload Configuration File
            button@@create.file.uploadButtonText"
          placeholder="Upload Configuration File "
          buttonLabel="Browse..."
          buttonAriaLabel="browse file"
          accept=".yaml, .json"
          class="uppercase-btn"
          [multiple]="true"
          (ngModel)="(files)"
          (selectedFilesChanged)="handleFileSelection($event)"
        >
        </fd-file-uploader>
        <app-help-popover
          i18n-title="
            Popover title|Title of the Upload File
            popover@@create.file.popover.title"
          [headerTitle]="'Upload Configuration File'"
          [body]="uploadFilePopoverBody"
          [width]="'500px'"
          [placement]="'bottom'"
        ></app-help-popover>
      </div>
      <ng-template
        #uploadFilePopoverBody
        i18n="
          XMSG|Body text of the Upload File popover@@create.file.popover.body"
      >
        You can create a configuration file and use it to quickly load the
        details about your new organization into the entry form. The file must
        contain the following information in either YAML or JSON format:
        <ul>
          <li>
            YAML:
            <ul style="list-style-type: none">
              <li>
                <pre [innerHTML]="yamlExample"></pre>
              </li>
            </ul>
          </li>
          <li>
            JSON:
            <ul style="list-style-type: none">
              <li>
                <pre>{{ jsonExample | json }}</pre>
              </li>
            </ul>
          </li>
        </ul>
        The "name" (technical name) and "displayName" fields are mandatory. For
        guidelines about providing the technical and display names, click the
        help buttons next to the respective fields in the input form.
      </ng-template>
      <div
        style="margin: 10px 0"
        i18n="
          XMSG|Text for manually entering organization
          properties@@create.general.enter"
      >
        Or enter the organization's properties in this form:
      </div>
    </div>
  }
  <form [formGroup]="form" autocomplete="off">
    <h3 class="section-title">Basic Info</h3>
    <app-container [body]="basicInfoContainer"></app-container>
    <ng-template #basicInfoContainer>
      <fd-form-input-message-group [triggers]="['mouseenter', 'mouseleave']">
        <app-helpful-label
          [required]="true"
          for="organization-displayname-input"
          i18n-label="XFLD|Label of the Name input@@create.name.input.label"
          i18n-popoverTitle="
            XTIT|Title of the Name popover@@create.name.popover.title"
          label="Name"
          popoverTitle="Name"
          [popoverBody]="namePopover"
        >
        </app-helpful-label>
        <ng-template
          #namePopover
          i18n="XMSG|Body text of the Name popover@@create.name.popover.body"
        >
          This is the display name of your organization. <br /><br />
          Examples: Industry Cloud, Cloud Fundamental Services <br /><br />
          You can change this name after the organization is created.
        </ng-template>
        <input
          fd-form-control
          fdCompact
          type="text"
          name="name"
          id="organization-displayname-input"
          placeholder="Enter a display name"
          formControlName="displayName"
          [disableControl]="!isNonSubresourceActionsEnabled"
          [state]="Utils.getValidationState(form.get('displayName'))"
          class="name-input dialog-form-input"
        />

        @if (
          (form.get("displayName")!.touched ||
            form.get("displayName")!.dirty) &&
          form.get("displayName")!.getError("required")
        ) {
          <fd-form-message [type]="'error'"> Name is required </fd-form-message>
        }
        @if (
          (form.get("displayName")!.touched ||
            form.get("displayName")!.dirty) &&
          form.get("displayName")!.getError("invalidDisplayName")
        ) {
          <fd-form-message [type]="'error'">
            Name can be only alphabetical letters (a-z), numbers (0-9), and the
            following special characters: ":.,-_#~/()!?&#64;'\&$%[]+;".
          </fd-form-message>
        }
        @if (
          (form.get("displayName")!.touched ||
            form.get("displayName")!.dirty) &&
          form.get("displayName")!.getError("maxLength")
        ) {
          <fd-form-message [type]="'error'">
            Name must not be longer than 100 characters.
          </fd-form-message>
        }
      </fd-form-input-message-group>
      <fd-form-input-message-group [triggers]="['mouseenter', 'mouseleave']">
        <app-helpful-label
          [required]="true"
          for="organization-name-input"
          i18n-label="
            Input Label|Label of the Technical Name
            input@@create.technicalName.input.label"
          i18n-popoverTitle="
            XTIT|Title of the Technical Name
            popover@@create.technicalName.popover.title"
          label="Technical Name"
          popoverTitle="Technical Name"
          [popoverBody]="technicalNamePopover"
        >
        </app-helpful-label>
        <ng-template
          #technicalNamePopover
          i18n="
            XMSG|Body text of the Technical Name
            popover@@create.technicalName.popover.body"
        >
          The technical name of your organization must:
          <ul>
            <li>
              Be DNS-compliant. It can contain only lowercase alphabetical
              characters (a-z), numeric characters (0-9), hyphens (-), and
              periods (.).
            </li>
            <li>Start and end with an alphanumeric character.</li>
          </ul>
          Examples: industry-cloud, cloud-fundamental-services <br /><br />
          IMPORTANT: You cannot change the technical name once the organization
          is created.
        </ng-template>
        <input
          fd-form-control
          fdCompact
          type="text"
          name="name"
          id="organization-name-input"
          placeholder="Enter a name"
          formControlName="name"
          [disableControl]="isEdit"
          [state]="Utils.getValidationState(form.get('name'))"
          class="name-input dialog-form-input"
        />

        @if (
          (form.get("name")!.touched || form.get("name")!.dirty) &&
          form.get("name")!.getError("required")
        ) {
          <fd-form-message [type]="'error'">
            Technical Name is required
          </fd-form-message>
        }
        @if (
          (form.get("name")!.touched || form.get("name")!.dirty) &&
          form.get("name")!.getError("invalidName")
        ) {
          <fd-form-message [type]="'error'">
            Technical Name must be DNS-compliant. It can contain only lowercase
            alphabetical letters (a-z), numbers (0-9), hyphens (-), and periods
            (.). It must start and end with a lowercase letter or number.
          </fd-form-message>
        }
        @if (
          (form.get("name")!.touched || form.get("name")!.dirty) &&
          form.get("name")!.getError("maxlength")
        ) {
          <fd-form-message [type]="'error'">
            Technical name must not be longer than 63 characters.
          </fd-form-message>
        }
      </fd-form-input-message-group>
      <fd-form-input-message-group [triggers]="['mouseenter', 'mouseleave']">
        <label fd-form-label for="organization-description-input"
          >Description</label
        >
        <input
          fd-form-control
          fdCompact
          type="text"
          name="name"
          id="organization-description-input"
          placeholder="Enter a description"
          formControlName="description"
          [disableControl]="!isNonSubresourceActionsEnabled"
          class="name-input dialog-form-input"
        />
        @if (
          (form.get("description")!.touched ||
            form.get("description")!.dirty) &&
          form.get("description")!.getError("invalidDescription")
        ) {
          <fd-form-message [type]="'error'">
            Description can be only alphabetical letters (a-z), numbers (0-9),
            and the following special characters: ":.,-_#~/()!?&#64;'\&$%[]+;".
          </fd-form-message>
        }
        @if (
          (form.get("description")!.touched ||
            form.get("description")!.dirty) &&
          form.get("description")!.getError("maxLength")
        ) {
          <fd-form-message [type]="'error'">
            Description must not be longer than 250 characters.
          </fd-form-message>
        }
      </fd-form-input-message-group>
    </ng-template>
  </form>

  <div class="separator"></div>
  @if (isEdit) {
    <div>
      <app-add-labels-and-annotations
        #addLabelsAndAnnotationsComponent
        [fixed]="true"
        [labels]="labels"
        [annotations]="annotations"
      ></app-add-labels-and-annotations>
    </div>
  }
</ng-template>
